import { useEffect, useMemo, useState } from 'react';
import { Building2, Loader2, Mail, Pencil, Plus, Rss, Search, Trash2, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { SourceTier, TraditionalMedium } from '@/client/types/media';
import { MEDIUM_LABEL } from '@/client/types/media';
import type { ApiMediaOutlet } from '@/client/types/marketing/api';
import Pagination from '@/client/components/common/Pagination';
import { TooltipProvider } from '@/components/ui/tooltip';
import { MediaPageShell } from './components/MediaPageShell';
import { OutletFavicon } from './components/OutletFavicon';
import { OutletFormDialog } from './components/OutletFormDialog';
import { OutletEmailDialog } from '@/client/pages/Admin/Marketing/components/OutletEmailDialog';
import { SourceContactsCell } from './components/SourceContactsCell';

/** Based there, read there, or read across Africa/worldwide. */
const reaches = (o: ApiMediaOutlet, code: string) =>
  o.country === code ||
  (o.audience_countries ?? []).some((c) => c === code || c === 'AFRICA' || c === 'GLOBAL');

/** Preferred email (or legacy contact_email) and preferred phone/WhatsApp. */
const preferredContacts = (o: ApiMediaOutlet): string[] => {
  const pts = o.contact_points ?? [];
  const email = pts.find((p) => p.kind === 'email' && p.preferred)?.value ?? o.contact_email;
  const phone =
    pts.find((p) => p.kind === 'phone' && p.preferred) ??
    pts.find((p) => p.kind === 'whatsapp' && p.preferred);
  return [email, phone?.value].filter((v): v is string => !!v);
};
import { ActionIconButton } from './components/ActionIconButton';
import {
  useDeleteMediaOutletMutation,
  useMediaOutletsQuery,
  useSourceCoverageCountsQuery,
} from '@/client/api/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';

/**
 * Sources → Outlets, on real data. Rows are keyed on `moid`.
 *
 * The Feed column is the one that earns its place: an outlet with a feed URL is
 * being ingested every 15 minutes, and one without is a directory entry nobody
 * is reading. That difference was invisible before, which is how a "why is Radar
 * empty" question ends up being a missing field on six rows.
 */

const MEDIUMS: TraditionalMedium[] = [
  'print',
  'online_news',
  'tv',
  'radio',
  'podcast',
  'wire',
  'other',
];

/** The API stores medium as a free string; fall back rather than render blank. */
const mediumLabel = (medium: string) =>
  MEDIUM_LABEL[medium as TraditionalMedium] ?? medium;

const SourcesOutletsPage = () => {
  const { data, isLoading, isError, error } = useMediaOutletsQuery({ limit: 200 });
  const coverageCounts = useSourceCoverageCountsQuery();
  const removeOutlet = useDeleteMediaOutletMutation();

  const [q, setQ] = useState('');
  const [medium, setMedium] = useState<TraditionalMedium | 'all'>('all');
  const [tier, setTier] = useState<SourceTier | 'all'>('all');
  const [country, setCountry] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ApiMediaOutlet | null>(null);
  const [deleting, setDeleting] = useState<ApiMediaOutlet | null>(null);
  const [emailing, setEmailing] = useState<ApiMediaOutlet | null>(null);

  const outlets: ApiMediaOutlet[] = data?.items ?? [];
  const coverageFor = (moid: number) => coverageCounts.data?.outlets?.[String(moid)] ?? 0;

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return outlets
      .filter((o) => (medium === 'all' ? true : o.medium === medium))
      .filter((o) => (tier === 'all' ? true : o.tier === tier))
      .filter((o) => country === 'all' || reaches(o, country))
      .filter((o) => {
        if (!query) return true;
        const beats = Array.isArray(o.beats) ? (o.beats as unknown[]) : [];
        return (
          o.name.toLowerCase().includes(query) ||
          (o.region?.toLowerCase().includes(query) ?? false) ||
          (o.owner_name?.toLowerCase().includes(query) ?? false) ||
          beats.some((b) => String(b).toLowerCase().includes(query))
        );
      })
      .sort((a, b) => {
        if (a.tier !== b.tier) return a.tier - b.tier;
        return a.name.localeCompare(b.name);
      });
  }, [outlets, medium, tier, country, q]);

  useEffect(() => {
    setPage(1);
  }, [q, medium, tier, country]);

  const countries = useMemo(
    () =>
      [
        ...new Set(
          outlets.flatMap((o) => [o.country, ...(o.audience_countries ?? [])]).filter(Boolean),
        ),
      ]
        .filter((c) => c !== 'AFRICA' && c !== 'GLOBAL')
        .sort() as string[],
    [outlets],
  );

  useEffect(() => {
    const max = Math.max(1, Math.ceil(filtered.length / limit) || 1);
    if (page > max) setPage(max);
  }, [filtered.length, limit, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, page, limit]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (outlet: ApiMediaOutlet) => {
    setEditing(outlet);
    setFormOpen(true);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const name = deleting.name;
    removeOutlet.mutate(
      { moid: deleting.moid },
      {
        onSuccess: () => toast.success(`Removed ${name}`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the outlet')),
      },
    );
    setDeleting(null);
  };

  const stat = (value: number) => (isLoading ? '—' : String(value));
  const ingesting = outlets.filter((o) => !!o.feed_url).length;

  return (
    <MediaPageShell
      title="Outlets"
      description="Press and broadcast outlets used as Studio destinations and Radar listen targets. Outlets with a feed URL are ingested automatically."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Sources' },
        { label: 'Outlets' },
      ]}
      icon={Building2}
      actions={
        <Button type="button" className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Add outlet
        </Button>
      }
      stats={[
        { label: 'Outlets', value: stat(outlets.length) },
        { label: 'Ingesting', value: stat(ingesting) },
        { label: 'Destinations', value: stat(outlets.filter((o) => o.use_as_destination).length) },
        { label: 'Mediums', value: stat(new Set(outlets.map((o) => o.medium)).size) },
      ]}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, region, owner, beat…"
            className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none ring-blue-500 focus:ring-2"
          />
        </div>
        <select
          value={medium}
          onChange={(e) => setMedium(e.target.value as TraditionalMedium | 'all')}
          className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
        >
          <option value="all">All mediums</option>
          {MEDIUMS.map((m) => (
            <option key={m} value={m}>
              {MEDIUM_LABEL[m]}
            </option>
          ))}
        </select>
        <select
          value={tier === 'all' ? 'all' : String(tier)}
          onChange={(e) => {
            const v = e.target.value;
            setTier(v === 'all' ? 'all' : (Number(v) as SourceTier));
          }}
          className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
        >
          <option value="all">All tiers</option>
          <option value="1">Tier 1</option>
          <option value="2">Tier 2</option>
          <option value="3">Tier 3</option>
        </select>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
          aria-label="Country"
        >
          <option value="all">All countries</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              Reaches {c}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <TooltipProvider delayDuration={200}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Outlet', 'Medium', 'Tier', 'Beats', 'Feed', 'Coverage', 'Contacts', 'Flags'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ),
                )}
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={9} className="px-6 py-16 text-center text-sm text-gray-500">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading outlets…
                  </td>
                </tr>
              )}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={9} className="px-6 py-16 text-center text-sm">
                    <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
                    <div className="font-medium text-amber-800">Could not load outlets</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {getApiErrorMessage(error, 'The marketing API did not respond')}
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading && !isError && outlets.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-16 text-center text-sm text-gray-500">
                    No outlets registered yet — add the publications you pitch, and give the ones
                    worth watching a feed URL so Radar starts reading them.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && outlets.length > 0 && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-16 text-center text-sm text-gray-500">
                    No outlets match these filters.
                  </td>
                </tr>
              )}

              {paged.map((out) => {
                const beats = Array.isArray(out.beats) ? (out.beats as unknown[]) : [];
                return (
                  <tr key={out.moid} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {out.website ? (
                          <a
                            href={out.website}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={out.website}
                          >
                            <OutletFavicon
                              name={out.name}
                              website={out.website}
                              feedUrl={out.feed_url}
                            />
                          </a>
                        ) : (
                          <OutletFavicon
                            name={out.name}
                            website={out.website}
                            feedUrl={out.feed_url}
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{out.name}</div>
                          <div className="text-xs text-gray-500">
                          {out.country ?? out.region ?? '—'}
                          {out.audience_countries?.length
                            ? ` · read in ${out.audience_countries.join(', ')}`
                            : ''}
                        </div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {!!out.use_as_destination && (
                              <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                                Destination
                              </span>
                            )}
                            {!!out.use_as_listen && (
                              <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                                Listen
                              </span>
                            )}
                          </div>
                          {out.notes && (
                            <div className="mt-0.5 line-clamp-1 text-[11px] text-gray-400">
                              {out.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {mediumLabel(out.medium)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">T{out.tier}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {beats.length ? beats.map(String).join(', ') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {out.feed_url ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-emerald-700"
                          title={out.feed_url}
                        >
                          <Rss className="h-3 w-3" />
                          {out.feed_last_fetched_at
                            ? new Date(out.feed_last_fetched_at).toLocaleDateString()
                            : 'not read yet'}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openEdit(out)}
                          className="text-xs text-blue-700 hover:underline"
                        >
                          Add feed
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {coverageCounts.isLoading ? '…' : coverageFor(out.moid)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <SourceContactsCell
                        sourceKind="outlet"
                        sourceId={out.moid}
                        fallback={preferredContacts(out)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {out.do_not_pitch ? (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          Do not pitch
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center justify-end gap-0.5">
                        {!!out.active && !!out.use_as_destination && !out.do_not_pitch && (
                          <ActionIconButton label="Email this outlet" onClick={() => setEmailing(out)}>
                            <Mail className="h-4 w-4" />
                          </ActionIconButton>
                        )}
                        <ActionIconButton label="Edit" onClick={() => openEdit(out)}>
                          <Pencil className="h-4 w-4" />
                        </ActionIconButton>
                        <ActionIconButton
                          label="Remove"
                          onClick={() => setDeleting(out)}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </ActionIconButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </TooltipProvider>
        {!isLoading && !isError && filtered.length > 0 && (
          <div className="space-y-2 border-t border-gray-100 px-4 py-3">
            <Pagination
              total={filtered.length}
              page={page}
              limit={limit}
              itemsPerPage={limit}
              setPage={setPage}
              setItemsPerPage={(n) => {
                setLimit(n);
                setPage(1);
              }}
              showSelect
              selectOptions={[10, 20, 50]}
              compact
            />
            <p className="text-xs text-gray-400">
              {ingesting} of {outlets.length} have a feed and are being read every 15 minutes
              {data?.total != null && data.total > outlets.length
                ? ` · ${data.total} total on server (showing first ${outlets.length})`
                : ''}
            </p>
          </div>
        )}
      </div>

      <OutletEmailDialog
        open={!!emailing}
        placement={null}
        outlet={emailing ?? undefined}
        onClose={() => setEmailing(null)}
      />

      <OutletFormDialog
        open={formOpen}
        outlet={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove outlet?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && (
                <>
                  Remove <strong>{deleting.name}</strong> from the outlet directory
                  {coverageFor(deleting.moid) > 0
                    ? `. ${coverageFor(deleting.moid)} linked coverage item(s) will keep their
                       content but lose this outlet link.`
                    : '.'}{' '}
                  {deleting.feed_url ? 'Its feed will stop being ingested.' : ''}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MediaPageShell>
  );
};

export default SourcesOutletsPage;
